import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, updateDoc, writeBatch, Timestamp, getDoc } from "firebase/firestore";
import { getCurrentUserCompanyId } from "../utils/userUtils";
import { APPROVAL_STATUS, createApprovalRequests } from "../utils/approvalUtils";

export default function RejectedSubmissionsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rejectedSubmissions, setRejectedSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resubmitting, setResubmitting] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    loadRejectedSubmissions();
    const interval = setInterval(loadRejectedSubmissions, 30000);
    return () => clearInterval(interval);
  }, []);

  async function loadRejectedSubmissions() {
    const user = auth.currentUser;
    if (!user) return;

    try {
      setLoading(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const submissionsRef = collection(db, "companies", companyId, "submissions");
      const q = query(
        submissionsRef,
        where("conductedBy", "==", user.uid),
        where("approvalStatus", "in", [APPROVAL_STATUS.REJECTED, APPROVAL_STATUS.CHANGES_REQUESTED])
      );

      const snapshot = await getDocs(q);
      const submissions = [];

      for (const docSnap of snapshot.docs) {
        const data = { id: docSnap.id, ...docSnap.data() };

        if (data.status === "draft") continue;

        const approvalsRef = collection(db, "companies", companyId, "submissions", docSnap.id, "approvals");
        const approvalsSnapshot = await getDocs(approvalsRef);
        data.approvals = approvalsSnapshot.docs.map(aDoc => ({
          id: aDoc.id,
          ...aDoc.data()
        }));

        submissions.push(data);
      }

      setRejectedSubmissions(submissions);
    } catch (error) {
      console.error("Error loading rejected submissions:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleResubmit(submission) {
    if (!window.confirm(t('approvals.confirmResubmit'))) {
      return;
    }

    setResubmitting(submission.id);

    try {
      const companyId = await getCurrentUserCompanyId();
      
      // Get the form's approval workflow config
      const workflowRef = doc(db, `companies/${companyId}/forms/${submission.formId}/settings/approvalWorkflow`);
      const workflowSnap = await getDoc(workflowRef);
      
      if (!workflowSnap.exists() || !workflowSnap.data().enabled) {
        alert(t('approvals.workflowNotConfigured'));
        setResubmitting(null);
        return;
      }

      const workflow = workflowSnap.data();
      const batch = writeBatch(db);

      // Delete existing approvals
      const approvalsRef = collection(db, "companies", companyId, "submissions", submission.id, "approvals");
      const approvalsSnapshot = await getDocs(approvalsRef);
      approvalsSnapshot.docs.forEach(aDoc => {
        batch.delete(aDoc.ref);
      });

      // Create new approval requests
      const approvalRequests = createApprovalRequests(workflow);
      approvalRequests.forEach(approval => {
        const newApprovalRef = doc(collection(db, "companies", companyId, "submissions", submission.id, "approvals"));
        batch.set(newApprovalRef, {
          ...approval,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      });

      // Update submission status
      const submissionRef = doc(db, "companies", companyId, "submissions", submission.id);
      batch.update(submissionRef, {
        approvalStatus: APPROVAL_STATUS.PENDING,
        resubmittedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      await batch.commit();

      alert(t('approvals.resubmitSuccess'));
      loadRejectedSubmissions(); // Refresh list
    } catch (error) {
      console.error("Error resubmitting:", error);
      alert(t('approvals.resubmitFailed'));
    } finally {
      setResubmitting(null);
    }
  }

  function getRejectionDetails(submission) {
    const rejectedApprovals = submission.approvals?.filter(a => 
      a.status === 'rejected' || a.status === 'changes-requested'
    ) || [];

    const details = [];
    rejectedApprovals.forEach(approval => {
      approval.approvedBy?.forEach(action => {
        if (action.decision === 'reject' || action.decision === 'request-changes') {
          details.push({
            stage: approval.stageName,
            approver: action.userName,
            decision: action.decision,
            comment: action.comment,
            timestamp: action.timestamp
          });
        }
      });
    });

    return details;
  }

  const shell = (content) => (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-24 -top-32 w-80 h-80 bg-red-600/20 blur-3xl rounded-full" />
        <div className="absolute right-16 top-10 w-96 h-96 bg-rose-500/18 blur-3xl rounded-full" />
        <div className="absolute -right-32 bottom-16 w-72 h-72 bg-amber-400/12 blur-3xl rounded-full" />
      </div>
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-10 lg:py-14">
        {content}
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <div className="flex flex-col items-center justify-center py-16">
        <div className="h-12 w-12 border-2 border-rose-400/60 border-t-transparent rounded-full animate-spin mb-4" />
        <div className="text-gray-200 text-lg">{t('approvals.loadingRejected')}</div>
      </div>
    );
  }

  const emptyState = rejectedSubmissions.length === 0;

  return shell(
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{t('approvals.approvalsLabel')}</p>
          <h1 className="text-3xl font-bold text-white">{t('approvals.rejectedChangesRequested')}</h1>
          <p className="text-sm text-gray-300">{t('approvals.checksNeedFixes')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition"
          >
            ← {t('common.back')}
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-900/30 transition"
          >
            🏠 {t('approvals.home')}
          </button>
        </div>
      </div>

      {emptyState ? (
        <div className="text-center bg-white/5 border border-white/10 rounded-3xl p-12 backdrop-blur-lg shadow-2xl">
          <div className="text-5xl mb-4">✅</div>
          <h3 className="text-2xl font-semibold text-white mb-2">{t('approvals.noRejectedSubmissions')}</h3>
          <p className="text-sm text-gray-300 mb-2">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p className="text-sm text-gray-400 max-w-xl mx-auto">
            {t('approvals.allApprovedOrPending')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {rejectedSubmissions.map((submission) => {
            const rejectionDetails = getRejectionDetails(submission);
            const completedAt = submission.completedAt?.toDate();
            const daysAgo = completedAt ? Math.floor((Date.now() - completedAt.getTime()) / (1000 * 60 * 60 * 24)) : 0;
            const isRejected = submission.approvalStatus === APPROVAL_STATUS.REJECTED;

            return (
              <div
                key={submission.id}
                className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-lg shadow-xl hover:-translate-y-0.5 transition transform"
              >
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div className="flex-1 space-y-1">
                    <h3 className="font-semibold text-white text-lg leading-tight">{submission.formTitle || t('approvals.untitledForm')}</h3>
                    <div className="text-sm text-gray-200 space-y-1">
                      <div>📄 {submission.documentNumber || t('common.na')}</div>
                      <div>👤 {submission.conductedByName || t('common.unknown')}</div>
                      <div>📅 {completedAt ? completedAt.toLocaleDateString() : t('approvals.dateUnavailable')} ({daysAgo === 0 ? t('approvals.today') : t('approvals.daysAgo', { count: daysAgo })})</div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                    isRejected
                      ? "bg-red-500/20 text-red-100 border-red-300/30"
                      : "bg-amber-400/15 text-amber-100 border-amber-200/40"
                  }`}>
                    {isRejected ? t('approvals.rejected') : t('approvals.changesRequested')}
                  </span>
                </div>

                <div className="mb-4 p-3 bg-black/30 border border-white/10 rounded-xl">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                    <span>⚠️</span>
                    <span>{t('approvals.reviewerFeedback')}</span>
                  </div>
                  <div className="space-y-3">
                    {rejectionDetails.length === 0 ? (
                      <p className="text-sm text-gray-300">{t('approvals.noFeedbackRecorded')}</p>
                    ) : (
                      rejectionDetails.slice(0, 2).map((detail, idx) => (
                        <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3">
                          <div className="flex items-start justify-between text-sm mb-2">
                            <span className="font-semibold text-white">{detail.stage}</span>
                            <span className="text-xs text-gray-400">{detail.timestamp?.toDate().toLocaleDateString()}</span>
                          </div>
                          <p className="text-sm text-gray-200">👤 {detail.approver || t('approvals.reviewer')}</p>
                          {detail.comment && (
                            <p className="text-sm text-gray-100 bg-white/5 border border-white/10 rounded-md p-2 mt-2">💬 {detail.comment}</p>
                          )}
                        </div>
                      ))
                    )}
                    {rejectionDetails.length > 2 && (
                      <p className="text-xs text-gray-400">{t('approvals.moreFeedbackItems', { count: rejectionDetails.length - 2 })}</p>
                    )}
                  </div>
                </div>

                {submission.responses && submission.responses.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-100 mb-2">📋 {t('approvals.snapshotOfResponses')}</h4>
                    <div className="space-y-2 text-sm text-gray-200">
                      {submission.responses.slice(0, 3).map((resp, idx) => (
                        <div key={idx} className="bg-white/5 border border-white/5 rounded-lg p-2">
                          <span className="font-medium text-white">{resp.label}:</span>{" "}
                          <span className="text-gray-200">
                            {typeof resp.value === 'object' ? JSON.stringify(resp.value) : resp.value || t('common.na')}
                          </span>
                        </div>
                      ))}
                      {submission.responses.length > 3 && (
                        <p className="text-xs text-gray-400 italic">{t('approvals.moreResponses', { count: submission.responses.length - 3 })}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setSelectedSubmission(submission)}
                    className="flex-1 min-w-[160px] px-4 py-3 bg-white/10 border border-white/15 text-gray-100 rounded-xl hover:bg-white/15 transition"
                  >
                    {t('approvals.viewDetails')}
                  </button>
                  <button
                    onClick={() => handleResubmit(submission)}
                    disabled={resubmitting === submission.id}
                    className={`flex-1 min-w-[160px] px-4 py-3 rounded-xl font-semibold transition-all ${
                      resubmitting === submission.id
                        ? "bg-white/10 text-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-emerald-500 to-rose-500 hover:from-emerald-400 hover:to-rose-400 text-white shadow-lg shadow-rose-900/30"
                    }`}
                  >
                    {resubmitting === submission.id ? t('approvals.resubmitting') : t('approvals.resubmitForReview')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-900 text-gray-50 border border-white/10 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{t('approvals.rejectedSubmission')}</p>
                  <h3 className="text-2xl font-bold">{selectedSubmission.formTitle || t('approvals.untitledForm')}</h3>
                  <p className="text-sm text-gray-300">{t('approvals.document')} {selectedSubmission.documentNumber || t('common.na')} • {selectedSubmission.conductedByName || t('approvals.unknownUser')}</p>
                </div>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="text-sm text-gray-300 hover:text-white"
                  disabled={resubmitting === selectedSubmission.id}
                >
                  ✕ {t('common.close')}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-2">
                  <h4 className="font-semibold text-white">{t('approvals.submissionDetails')}</h4>
                  <div className="text-sm text-gray-200 space-y-1">
                    <div>📄 {t('approvals.document')}: {selectedSubmission.documentNumber || t('common.na')}</div>
                    <div>👤 {t('approvals.conductor')}: {selectedSubmission.conductedByName || t('common.unknown')}</div>
                    <div>📅 {t('approvals.completed')}: {selectedSubmission.completedAt?.toDate().toLocaleString() || t('common.na')}</div>
                    <div>⚑ {t('approvals.status')}: {selectedSubmission.approvalStatus || t('common.na')}</div>
                  </div>
                </div>

                <div className="p-4 bg-black/30 border border-white/10 rounded-xl space-y-2">
                  <h4 className="font-semibold text-white">{t('approvals.reviewerFeedback')}</h4>
                  <div className="space-y-3 text-sm">
                    {getRejectionDetails(selectedSubmission).length === 0 ? (
                      <p className="text-gray-300">{t('approvals.noFeedbackRecorded')}</p>
                    ) : (
                      getRejectionDetails(selectedSubmission).map((detail, idx) => (
                        <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3">
                          <div className="flex items-start justify-between text-sm mb-2">
                            <span className="font-semibold text-white">{detail.stage}</span>
                            <span className="text-xs text-gray-400">{detail.timestamp?.toDate().toLocaleString()}</span>
                          </div>
                          <p className="text-sm text-gray-200">👤 {detail.approver || t('approvals.reviewer')}</p>
                          {detail.comment && <p className="text-sm text-gray-100 bg-white/5 border border-white/10 rounded-md p-2 mt-2">💬 {detail.comment}</p>}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {selectedSubmission.responses && selectedSubmission.responses.length > 0 && (
                <div className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-white">{t('approvals.responsesFirstTen')}</h4>
                    {selectedSubmission.responses.length > 10 && (
                      <span className="text-xs text-gray-400">{t('approvals.moreItems', { count: selectedSubmission.responses.length - 10 })}</span>
                    )}
                  </div>
                  <div className="max-h-96 overflow-y-auto space-y-3">
                    {selectedSubmission.responses.slice(0, 10).map((resp, idx) => (
                      <div key={idx} className="bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-gray-100">
                        <div className="text-xs font-medium text-gray-300 mb-1">{resp.label}</div>
                        <div className="text-gray-100">
                          {typeof resp.value === 'object' ? JSON.stringify(resp.value) : resp.value || t('common.na')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="px-6 py-3 bg-white/5 border border-white/10 text-gray-100 rounded-lg hover:bg-white/10 transition"
                  disabled={resubmitting === selectedSubmission.id}
                >
                  {t('common.close')}
                </button>
                <button
                  onClick={() => handleResubmit(selectedSubmission)}
                  disabled={resubmitting === selectedSubmission.id}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    resubmitting === selectedSubmission.id
                      ? "bg-white/10 text-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-emerald-500 to-rose-500 hover:from-emerald-400 hover:to-rose-400 text-white shadow-lg shadow-rose-900/30"
                  }`}
                >
                  {resubmitting === selectedSubmission.id ? t('approvals.resubmitting') : t('approvals.resubmitForReview')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
